<div class="overview-container">
  @if (loading) {
    <mat-progress-bar mode="indeterminate" class="loading-progress"></mat-progress-bar>
  }

  @if (!loading) {
    <div class="overview-content">
      <!-- Region detail card (Issue #1228) -->
      @if (activeRegion) {
        <div class="region-detail-card">
          <div class="region-detail-header">
            <mat-icon class="region-detail-icon">dashboard</mat-icon>
            <div class="region-detail-title-area">
              <h3 class="region-detail-title">{{ activeRegion.title || activeRegion.slug }}</h3>
              <div class="region-detail-meta">
                @if (activeRegion.ncsClassification) {
                  <span class="region-meta-chip">{{ activeRegion.ncsClassification }}</span>
                }
                @if (activeRegion.nodeKind) {
                  <span class="region-meta-chip region-meta-kind">{{ activeRegion.nodeKind }}</span>
                }
                <span class="region-meta-path">{{ activeRegion.path }}</span>
              </div>
            </div>
          </div>
          @if (regionDescriptionHtml) {
            <div class="region-detail-description" [innerHTML]="regionDescriptionHtml"></div>
          }
          @if (activeRegion.identifiersMap && activeRegion.identifiersMap.length > 0) {
            <div class="region-identifiers">
              @for (entry of activeRegion.identifiersMap; track entry[0]) {
                <span class="region-identifier-chip">{{ entry[0] }}: {{ entry[1] }}</span>
              }
            </div>
          }
          @if (regionTranscriptHtml) {
            <div class="region-transcript-section">
              <h4 class="region-section-title">Transcript</h4>
              <div class="region-transcript-content" [innerHTML]="regionTranscriptHtml"></div>
            </div>
          }
        </div>
      }

      @if (activePlanPage && !activeRegion) {
        <div class="metadata-container">
          <div class="metadata-header">
            <h3>Page Metadata</h3>
            <div class="metadata-actions">
              <button
                mat-mini-fab
                class="groom-button"
                (click)="groomMetadata()"
                [disabled]="isGrooming || savingMetadata"
                matTooltip="Groom metadata with AI"
                matTooltipPosition="below"
                aria-label="Groom metadata with AI"
              >
                @if (isGrooming) {
                  <mat-spinner diameter="24"></mat-spinner>
                } @else {
                  <mat-icon>auto_awesome</mat-icon>
                }
              </button>
              @if (hasUnsavedChanges) {
                <div class="action-buttons">
                  <button
                    mat-mini-fab
                    class="discard-button"
                    (click)="discardChanges()"
                    [disabled]="savingMetadata"
                    matTooltip="Discard changes"
                    matTooltipPosition="below"
                    aria-label="Discard metadata changes"
                  >
                    <mat-icon>undo</mat-icon>
                  </button>
                  <button
                    mat-mini-fab
                    class="save-button"
                    (click)="saveMetadata()"
                    [disabled]="savingMetadata"
                    [matTooltip]="savingMetadata ? 'Saving...' : 'Save changes'"
                    matTooltipPosition="below"
                    aria-label="Save metadata changes"
                  >
                    <mat-icon>{{ savingMetadata ? 'hourglass_empty' : 'check' }}</mat-icon>
                  </button>
                </div>
              }
            </div>
          </div>
          <form [formGroup]="metadataForm" class="metadata-form-wrapper">
            <div class="metadata-fields">
              <mat-form-field appearance="fill" class="field-title">
                <mat-label>Page Title</mat-label>
                <input matInput formControlName="title" [readonly]="savingMetadata" placeholder="e.g. FLOOR PLAN" />
                @if (titleChanged) {
                  <mat-hint class="field-changed-hint">Unsaved changes</mat-hint>
                }
              </mat-form-field>
              <mat-form-field appearance="fill" class="field-sheet-id">
                <mat-label>Sheet ID</mat-label>
                <input matInput formControlName="sheetId" [readonly]="savingMetadata" placeholder="e.g. A101" />
                @if (sheetIdChanged) {
                  <mat-hint class="field-changed-hint">Unsaved changes</mat-hint>
                }
              </mat-form-field>
              <mat-form-field appearance="fill" class="field-page-number">
                <mat-label>Page #</mat-label>
                <input matInput [value]="activePlanPage.getPageNumber()" disabled />
              </mat-form-field>
            </div>
            <mat-form-field appearance="fill" class="field-summary">
              <mat-label>Summary</mat-label>
              <textarea
                matInput
                formControlName="summary"
                [readonly]="savingMetadata"
                rows="3"
                placeholder="Brief summary of this page..."
              ></textarea>
              @if (summaryChanged) {
                <mat-hint class="field-changed-hint">Unsaved changes</mat-hint>
              }
            </mat-form-field>
          </form>
          @if (activePlanPage.getModelStamp(); as stamp) {
            @if (stamp.getModelName()) {
              <div class="model-metadata">
                Model: {{ stamp.getModelName() }}
                @if (stamp.getProfileId()) {
                  ({{ stamp.getProfileId() }})
                }
                @if (stamp.getCreatedTime()) {
                  | {{ modelStampDate | date: 'yyyy/MM/dd HH:mm' }}
                }
              </div>
            }
          }
        </div>
      }
      <!-- Nested Tab Group -->
      @if (activePlanPage && !activeRegion) {
        <div class="nested-tabs-section">
          <mat-tab-group
            [mat-stretch-tabs]="false"
            [selectedIndex]="activeNestedTabIndex"
            (selectedTabChange)="onNestedTabChange($event)"
            class="nested-tab-group"
          >
            <!-- Explanation Tab -->
            <mat-tab label="Explanation">
              <ng-template matTabContent>
                <app-details></app-details>
              </ng-template>
            </mat-tab>
            <!-- Transcript Tab -->
            <mat-tab label="Transcript">
              <ng-template matTabContent>
                <app-raw-markdown></app-raw-markdown>
              </ng-template>
            </mat-tab>
            <!-- Compliance Tab -->
            <mat-tab label="Compliance">
              <ng-template matTabContent>
                <div class="compliance-tab-content">
                  @if (isLoadingComplianceSummary) {
                    <div class="compliance-summary-loading">Loading compliance summary...</div>
                  }
                  @if (!isLoadingComplianceSummary && complianceSummaryHtml) {
                    <div class="compliance-summary-block">
                      <h3 class="compliance-summary-title">Compliance Summary</h3>
                      <div class="compliance-summary-content" [innerHTML]="complianceSummaryHtml"></div>
                    </div>
                  }
                </div>
              </ng-template>
            </mat-tab>
          </mat-tab-group>
        </div>
      }
    </div>
  }
</div>
